import { Link, usePage } from "@inertiajs/react";
import {
  Activity,
  ArrowLeft,
  BookOpen,
  Building,
  CheckCircle2,
  Component,
  Contact,
  DollarSign,
  FileText,
  FolderTree,
  LayoutGrid,
  Package,
  Receipt,
  Store,
  Truck,
  Users,
} from "lucide-react";

import { NavFooter } from "@/components/nav-footer";
import { NavMain } from "@/components/nav-main";
import { NavPersonal } from "@/components/nav-personal";
import { NavUser } from "@/components/nav-user";
import { OrgSwitcher } from "@/components/org-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { useAnnouncementStatus } from "@/hooks/use-announcement-status";
import {
  organizationApprovalsPath,
  organizationCategoriesPath,
  organizationCompaniesPath,
  organizationDailyActivityPath,
  organizationDashboardPath,
  organizationDeliveryNotesPath,
  organizationInvoicesPath,
  organizationMembersPath,
  organizationPricesPath,
  organizationProductsPath,
  organizationPurchaseRequestsPath,
  organizationSectorsPath,
  organizationSuppliersPath,
  organizationsPath,
  userManualPath,
} from "@/routes";
import type { NavItem, SharedData } from "@/types";

import AppLogo from "./app-logo";

const buildMainNavItems = (
  organizationSlug?: string | null,
  canViewDailyActivity = false,
): NavItem[] => {
  if (!organizationSlug) {
    return [];
  }

  const items: NavItem[] = [
    {
      title: "Dashboard",
      href: organizationDashboardPath(organizationSlug),
      icon: LayoutGrid,
    },
    ...(canViewDailyActivity
      ? [
          {
            title: "Actividad Diaria",
            href: organizationDailyActivityPath(organizationSlug),
            icon: Activity,
          },
        ]
      : []),
    {
      title: "Solicitudes Compras",
      href: organizationPurchaseRequestsPath(organizationSlug),
      icon: FileText,
    },
    {
      title: "Remitos",
      href: organizationDeliveryNotesPath(organizationSlug),
      icon: Truck,
    },
    {
      title: "Facturas",
      href: organizationInvoicesPath(organizationSlug),
      icon: Receipt,
    },
    {
      title: "Solicitudes por aprobar",
      href: organizationApprovalsPath(organizationSlug),
      icon: CheckCircle2,
    },
    {
      title: "Productos",
      href: organizationProductsPath(organizationSlug),
      icon: Package,
    },
    {
      title: "Histórico de Precios",
      href: organizationPricesPath(organizationSlug),
      icon: DollarSign,
    },
    {
      title: "Sectores/Obras",
      href: organizationSectorsPath(organizationSlug),
      icon: Building,
    },
    {
      title: "Compañías",
      href: organizationCompaniesPath(organizationSlug),
      icon: Component,
    },
    {
      title: "Categorías",
      href: organizationCategoriesPath(organizationSlug),
      icon: FolderTree,
    },
    {
      title: "Proveedores",
      href: organizationSuppliersPath(organizationSlug),
      icon: Store,
    },
  ];

  items.push({
    title: "Miembros",
    href: organizationMembersPath(organizationSlug),
    icon: Users,
  });

  return items;
};

const footerNavItems: NavItem[] = [
  // Example add items to navbar
  {
    title: "Soporte",
    href: "https://tally.so/r/nre9Ll",
    icon: Contact,
  },
  {
    title: "Manual de uso",
    href: userManualPath(),
    icon: BookOpen,
  },
];

export function AppSidebar() {
  const { auth, ui } = usePage<SharedData>().props;
  const hasNewAnnouncements = useAnnouncementStatus(ui?.announcements_version);
  const organizationSlug = auth.current_organization?.slug ?? null;
  const canViewDailyActivity =
    auth.groups?.includes("owner") || auth.groups?.includes("supervisor");

  // Crear items de navegación con el indicador para Dashboard
  const navItemsWithIndicator = buildMainNavItems(
    organizationSlug,
    canViewDailyActivity,
  ).map(
    (item) => {
      if (item.title === "Dashboard") {
        return {
          ...item,
          hasIndicator: hasNewAnnouncements,
        };
      }
      return item;
    },
  );

  return (
    <Sidebar collapsible="icon" variant="inset" hoverToExpand>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              asChild
              className="group-data-[collapsible=icon]:!p-0"
            >
              <Link
                href={
                  organizationSlug
                    ? organizationDashboardPath(organizationSlug)
                    : organizationsPath()
                }
                prefetch
              >
                <AppLogo />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <OrgSwitcher />
        {!organizationSlug && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <Link href={organizationsPath()}>
                  <ArrowLeft className="h-4 w-4" />
                  <span>Volver a organizaciones</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        <NavPersonal />
        <SidebarSeparator className="my-2" />
        <NavMain items={navItemsWithIndicator} />
      </SidebarContent>

      <SidebarFooter>
        <NavFooter items={footerNavItems} className="mt-auto" />
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
